import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { v4 as uuidv4 } from 'uuid';
import { useToast } from './toast';

export interface ImageFile {
  key: string;
  File: Blob;
}

interface ImageClipboard {
  imageFile: ImageFile[];
  removeImage(id: string): void;
  addImageClipboard(evt: ClipboardEvent): void;
}

const ImageClipboardContext = createContext<ImageClipboard>(
  {} as ImageClipboard
);

function useImageClipboard(): ImageClipboard {
  const context = useContext(ImageClipboardContext);
  if (!context) {
    throw new Error('useImageClipboard must be used within an AuthProvider');
  }
  return context;
}

const ImageClipboardProvider: React.FC = ({ children }) => {
  const { addToast } = useToast();

  const [imageFile, setImageFile] = useState<ImageFile[]>([]);

  const addImageClipboard = useCallback(
    (evt: ClipboardEvent) => {
      if (evt.clipboardData === null) return;

      // Use setImageFile dentro desta função de callback
      setImageFile((currentImageFile) => {
        if (currentImageFile.length > 9) {
          addToast({
            type: 'warning',
            title: 'Anexo Imagem',
            description: `Você não pode anexar mais do que 10 imagens.`,
          });
          return currentImageFile; // Retorna o valor atual sem modificação
        }

        if (evt.clipboardData === null) return currentImageFile;
        const clipboardItems = evt.clipboardData.items;
        const items: DataTransferItem[] = [].slice
          .call(clipboardItems)
          .filter((item: DataTransferItem) => {
            return item.type.indexOf('image') !== -1;
          });

        if (items.length === 0) return currentImageFile;

        const item = items[0];
        const blob = item.getAsFile();

        if (blob === null) return currentImageFile;

        return [...currentImageFile, { key: uuidv4(), File: blob }];
      });
    },
    [addToast]
  );

  const removeImage = (id: string): void => {
    setImageFile((img) => [...img.filter((i) => i.key !== id)]);
  };

  const valueContext = useMemo(
    () => ({
      imageFile,
      removeImage,
      addImageClipboard,
    }),
    [imageFile, removeImage, addImageClipboard]
  );

  return (
    <ImageClipboardContext.Provider value={valueContext}>
      {children}
    </ImageClipboardContext.Provider>
  );
};

export { ImageClipboardProvider, useImageClipboard };
